/**
=========================================================
* Trust Cargo - Containers
=========================================================

* One tab per container type - "Mix goods", "Light goods", whatever the
* office adds next - because a type is a row in container_types, not a value
* baked into this file. New tabs are created from the toolbar.
*
* Types are a schema change (see database/container_types.sql). Until it has
* been applied the type endpoints answer with an empty list, and this page
* renders exactly as it did before: one flat list, no tabs, nothing broken.
*/

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import { Table, TableCell, TableHead, TableBody, TableRow } from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import FilterBar, { filterRows } from "components/FilterBar";
import {
  FilterSelect,
  FilterDates,
  optionsFromRows,
  matches,
  inDateRange,
} from "components/FilterBar/controls";
import Pagination, { usePagination } from "components/Pagination";
import IdCell, { IdHead } from "components/IdCell";
import AddDialog from "components/AddDialog";
import RowActions from "components/RowActions";
import { boardAction } from "utils/board";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

const API = "https://trust-cargo.co/anas_backend/api";
const V2 = `${API}/v2`;

// the tab for containers that have not been given a type
const UNTYPED = "none";

/** A cargo total, or a dash when the container has no lines at all. */
const num = (v, places) =>
  v === null || v === undefined || v === ""
    ? "-"
    : Number(v).toLocaleString("en-US", {
        minimumFractionDigits: places,
        maximumFractionDigits: places,
      });

/**
 * Whole days from today to the estimated arrival - negative once that date
 * has gone by, null when there is no date to count to.
 *
 * Both ends are taken at local midnight so the answer is a count of
 * calendar days: an arrival "tomorrow" reads as 1 whatever the hour.
 */
function daysToArrival(date) {
  if (!date) return null;

  const arrival = new Date(`${String(date).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(arrival.getTime())) return null;

  const now = new Date();
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  return Math.round((arrival - midnight) / 86400000);
}

/**
 * "كم يوم ضايل" - how long until it lands, read straight off the row.
 *
 * It is only ever as good as the estimated arrival date, so a container
 * without one shows a dash rather than a number nobody can act on.
 */
function DaysLeft({ date }) {
  const days = daysToArrival(date);

  if (days === null) return "-";

  let label = `${days} d`;
  let tone = "dark";

  if (days < 0) {
    // already here - kept as a count rather than just "arrived", because
    // how long ago is what says whether it is late being emptied
    label = `${Math.abs(days)} d ago`;
    tone = "success";
  } else if (days === 0) {
    label = "Today";
    tone = "warning";
  } else if (days <= 7) {
    // the week that needs the paperwork ready
    tone = "warning";
  }

  return (
    <Chip
      label={label}
      size="small"
      sx={({ palette, functions: { rgba } }) => ({
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
        backgroundColor: rgba(palette[tone].main, tone === "dark" ? 0.08 : 0.16),
        color: tone === "dark" ? palette.dark.main : palette[tone].main,
      })}
    />
  );
}

DaysLeft.defaultProps = { date: null };
DaysLeft.propTypes = { date: PropTypes.string };

function Containers() {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  // the new/rename type dialog: {id, name} or null
  const [typeForm, setTypeForm] = useState(null);
  const [typeError, setTypeError] = useState("");
  const [status, setStatus] = useState("");
  const [telex, setTelex] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const navigate = useNavigate();

  // types only exist once the schema change has been applied
  const typed = types.length > 0;

  const fetchData = async () => {
    try {
      /**
       * Prefer the endpoint that carries container_type_id. Falling back to
       * the legacy list keeps the page working on a server that has not been
       * updated - it simply has no types to group by.
       */
      const [t, c] = await Promise.all([
        fetch(`${V2}/container-types`, { cache: "no-store" })
          .then((r) => (r.ok ? r.json() : []))
          .catch(() => []),
        fetch(`${V2}/typed-containers`, { cache: "no-store" }).catch(() => null),
      ]);

      setTypes(Array.isArray(t) ? t : []);

      if (c && c.ok) {
        setRows(await c.json());
      } else {
        const legacy = await fetch(`${API}/containers`).then((r) => r.json());
        setRows(legacy.containers || []);
      }
    } catch (e) {
      setRows([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, []);

  const byTab = (() => {
    if (tab === "all") return rows;
    if (tab === UNTYPED) return rows.filter((r) => !r.container_type_id);
    return rows.filter((r) => String(r.container_type_id) === String(tab));
  })();

  const narrowed = byTab.filter(
    (c) =>
      matches(c.status, status) &&
      matches(c.telex_status, telex) &&
      inDateRange(c.loading_date, from, to)
  );

  const filteredRows = filterRows(narrowed, query);
  const pager = usePagination(filteredRows);

  const untypedCount = rows.filter((r) => !r.container_type_id).length;

  /**
   * A container with nothing loaded sends total_cbm: null; a server running
   * the old controller does not send the key at all. Told apart so the empty
   * columns say why they are empty instead of looking like real zeroes.
   */
  const hasTotals = rows.length === 0 || rows.some((r) => "total_cbm" in r);
  const typeName = (id) => types.find((t) => String(t.id) === String(id))?.name;

  const removeOne = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete container ${row.name}?`)) return;

    try {
      await axios.post(
        `${API}/remove_container`,
        { id: row.id },
        { headers: { Accept: "application/json" } }
      );
      await fetchData();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e?.response?.data?.message || "Could not delete");
    }
  };

  /** Create or rename a type, i.e. add or rename a tab. */
  const saveType = async () => {
    const name = String(typeForm?.name || "").trim();
    if (!name) return;

    setBusy(true);
    setTypeError("");

    try {
      if (typeForm.id) {
        await axios.put(`${V2}/container-types/${typeForm.id}`, { name });
      } else {
        await axios.post(`${V2}/container-types`, { name });
      }
      setTypeForm(null);
      await fetchData();
    } catch (e) {
      setTypeError(e?.response?.data?.message || "Could not save the type");
    }
    setBusy(false);
  };

  const deleteType = async (t) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the "${t.name}" tab? Its containers move to Not set.`)) return;

    setBusy(true);
    try {
      await axios.delete(`${V2}/container-types/${t.id}`);
      if (String(tab) === String(t.id)) setTab("all");
      await fetchData();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e?.response?.data?.message || "Could not delete the type");
    }
    setBusy(false);
  };

  const cell = { fontSize: 14 };
  // tabular figures so the digits sit in the same place on every row
  const numCell = { ...cell, fontVariantNumeric: "tabular-nums", fontWeight: 600 };

  const tabSx = (active) => ({
    fontWeight: active ? 700 : 500,
    cursor: "pointer",
    backgroundColor: active ? "rgba(201,162,39,0.18)" : "transparent",
    border: "1px solid rgba(11,27,58,0.15)",
  });

  return (
    <DashboardLayout>
      <SuiBox py={1}>
        <SuiBox mb={3}>
          <Card>
            <FilterBar
              title="Containers"
              value={query}
              onChange={setQuery}
              count={filteredRows.length}
              total={byTab.length}
              placeholder="Search by name, number or status..."
              actions={
                <>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      setTypeError("");
                      setTypeForm({ name: "" });
                    }}
                  >
                    New Type
                  </Button>
                  <Button variant="contained" color="primary" onClick={() => setAdding(true)}>
                    Add Container
                  </Button>
                </>
              }
            >
              <FilterSelect
                label="Status"
                icon="flag"
                value={status}
                onChange={setStatus}
                options={optionsFromRows(rows, "status")}
              />
              <FilterSelect
                label="Telex"
                icon="description"
                value={telex}
                onChange={setTelex}
                options={optionsFromRows(rows, "telex_status")}
              />
              <FilterDates label="Loading" from={from} to={to} onFrom={setFrom} onTo={setTo} />

              {/* one tab per type; each carries its own count */}
              <Chip
                size="small"
                label={`All (${rows.length})`}
                onClick={() => setTab("all")}
                sx={tabSx(tab === "all")}
              />

              {types.map((t) => (
                <Chip
                  key={t.id}
                  size="small"
                  label={`${t.name} (${t.containers})`}
                  onClick={() => setTab(t.id)}
                  onDelete={() => deleteType(t)}
                  deleteIcon={<Icon sx={{ fontSize: "16px !important" }}>close</Icon>}
                  sx={tabSx(String(tab) === String(t.id))}
                />
              ))}

              {typed && untypedCount > 0 && (
                <Chip
                  size="small"
                  label={`Not set (${untypedCount})`}
                  onClick={() => setTab(UNTYPED)}
                  sx={tabSx(tab === UNTYPED)}
                />
              )}
            </FilterBar>

            {!typed && (
              <SuiBox px={3} pb={2}>
                <Alert severity="info">
                  Container types are not set up on the server yet, so there are no tabs. Run
                  database/container_types.sql once and upload the backend files.
                </Alert>
              </SuiBox>
            )}

            {!loading && !hasTotals && (
              <SuiBox px={3} pb={2}>
                <Alert severity="info">
                  T_CBM, T_KG and T_CTN are empty because this server is still running the old
                  controller - upload app/Http/Controllers/V2/ContainerTypeController.php. Days left
                  is worked out here and is already correct.
                </Alert>
              </SuiBox>
            )}

            {loading ? (
              <CargoLoader label="Loading containers" />
            ) : (
              <>
                <SuiBox sx={{ overflowX: "auto" }}>
                  <Table sx={{ minWidth: 1420 }} aria-label="containers table">
                    <TableHead>
                      <TableRow>
                        <IdHead />
                        <TableCell align="center">Name</TableCell>
                        <TableCell align="center">Number</TableCell>
                        {typed && <TableCell align="center">Type</TableCell>}
                        <TableCell align="center">Status</TableCell>
                        {/* what is inside: حجم / وزن / عدد كراتين */}
                        <TableCell align="center">T_CBM</TableCell>
                        <TableCell align="center">T_KG</TableCell>
                        <TableCell align="center">T_CTN</TableCell>
                        <TableCell align="center">Loading</TableCell>
                        <TableCell align="center">Est. arrival</TableCell>
                        <TableCell align="center">Days left</TableCell>
                        <TableCell align="center">Operations</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {pager.pageRows.map((row) => (
                        <TableRow key={row.id}>
                          <IdCell value={row.id} />
                          <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                            {row.name ?? "-"}
                          </TableCell>
                          <TableCell align="center" sx={cell}>
                            {row.number && row.number !== "0" ? row.number : "-"}
                          </TableCell>

                          {typed && (
                            <TableCell align="center" sx={cell}>
                              {typeName(row.container_type_id) ? (
                                <Chip
                                  label={typeName(row.container_type_id)}
                                  size="small"
                                  sx={({ palette, functions: { rgba } }) => ({
                                    fontWeight: 700,
                                    backgroundColor: rgba(palette.info.main, 0.14),
                                    color: palette.info.main,
                                  })}
                                />
                              ) : (
                                "-"
                              )}
                            </TableCell>
                          )}

                          <TableCell align="center" sx={cell}>
                            {row.status ? (
                              <Chip
                                label={row.status}
                                size="small"
                                sx={({ palette, functions: { rgba } }) => ({
                                  fontWeight: 700,
                                  textTransform: "capitalize",
                                  backgroundColor: rgba(palette.primary.main, 0.16),
                                  color: palette.dark.main,
                                })}
                              />
                            ) : (
                              "-"
                            )}
                          </TableCell>

                          {/* figures line up down the column, so two
                              containers can be compared at a glance */}
                          <TableCell align="center" sx={numCell}>
                            {num(row.total_cbm, 3)}
                          </TableCell>
                          <TableCell align="center" sx={numCell}>
                            {num(row.total_kg, 2)}
                          </TableCell>
                          <TableCell align="center" sx={numCell}>
                            {num(row.total_ctn, 0)}
                          </TableCell>

                          <TableCell align="center" sx={cell}>
                            {row.loading_date ?? "-"}
                          </TableCell>
                          <TableCell align="center" sx={cell}>
                            {row.estimated_arrival_date ?? "-"}
                          </TableCell>

                          <TableCell align="center" sx={cell}>
                            <DaysLeft date={row.estimated_arrival_date} />
                          </TableCell>

                          <TableCell align="center" sx={cell}>
                            <RowActions
                              actions={[
                                boardAction("container", row, fetchData),
                                {
                                  icon: "visibility",
                                  label: "View container",
                                  tone: "info",
                                  onClick: () => navigate(`/view_container/${row.id}`),
                                },
                                {
                                  icon: "list_alt",
                                  label: "Orders in this container",
                                  tone: "primary",
                                  onClick: () => navigate(`/container_orders/${row.id}`),
                                },
                                {
                                  icon: "edit",
                                  label: "Edit container",
                                  onClick: () => setEditingId(row.id),
                                },
                                {
                                  icon: "delete",
                                  label: "Delete",
                                  tone: "danger",
                                  onClick: () => removeOne(row),
                                },
                              ]}
                            />
                          </TableCell>
                        </TableRow>
                      ))}

                      {filteredRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={typed ? 12 : 11} align="center" sx={{ py: 4 }}>
                            <SuiTypography variant="button" color="text">
                              No containers
                            </SuiTypography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </SuiBox>
                <Pagination {...pager} />
              </>
            )}
          </Card>
        </SuiBox>
      </SuiBox>

      {/* the container form goes through the typed endpoints when they exist,
          so the type is saved with the rest in one request */}
      <AddDialog
        open={adding}
        entity={typed ? "container_typed" : "container"}
        onClose={() => setAdding(false)}
        onCreated={fetchData}
      />

      <AddDialog
        open={Boolean(editingId)}
        entity={typed ? "container_typed" : "container"}
        recordId={editingId}
        onClose={() => setEditingId(null)}
        onCreated={fetchData}
      />

      {/* add or rename a tab */}
      <Dialog open={Boolean(typeForm)} onClose={() => setTypeForm(null)} fullWidth maxWidth="xs">
        <DialogTitle>{typeForm?.id ? "Rename type" : "New container type"}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {typeError && (
            <SuiBox mb={2}>
              <Alert severity="error">{typeError}</Alert>
            </SuiBox>
          )}
          <TextField
            autoFocus
            fullWidth
            label="Type name"
            placeholder="Mix goods"
            value={typeForm?.name || ""}
            onChange={(e) => setTypeForm((f) => ({ ...f, name: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && saveType()}
            InputLabelProps={{ shrink: true }}
            sx={{ "& .MuiInputBase-root": { minHeight: 52 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTypeForm(null)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={saveType} disabled={busy}>
            {busy ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Containers;
